import { yupResolver } from "@hookform/resolvers/yup";
import { Flex, Image, Link, Text } from "@wayflyer/flyui";
import { RhfDropdownField } from "@wayflyer/flyui-hook-form";
import { useState, type ReactNode } from "react";
import { useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import { useCustomerAppConfig } from "@wayflyer/undercarriage";
import * as Yup from "yup";

import { AuthCallbackPath } from "../../callback-paths";
import { usePlatformConnectorProperties } from "../../queries";
import { connectPostPath, useConnectionFlowsConfig } from "../../config";
import type { AmazonData } from "../../common/connect-by-post";
import { useConnectByPost } from "../../common/connect-by-post";
import { ConnectionsList } from "../../common/connections";
import { OAuthErrorBanner } from "../../common/oauth-error-banner";
import { MESSAGES } from "../../messages";

import { useAmazonRegionOptions } from "./amazon-region-options";

const CONNECTOR_SLUG = "amazonrutter";

interface AmazonConnectProps {
  postConnectPath: AuthCallbackPath;
  onInitiateConnection?: () => void;
}
export const AmazonConnect = ({ postConnectPath, onInitiateConnection }: AmazonConnectProps) => {
  const intl = useIntl();
  const {
    data: { customerDomain },
  } = useCustomerAppConfig();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { connectorsApiUrl } = useConnectionFlowsConfig();
  const { data: connectorProperties } = usePlatformConnectorProperties(
    customerDomain,
    CONNECTOR_SLUG,
    postConnectPath,
    connectorsApiUrl
  );

  const connectByPost = useConnectByPost();

  const { regionOptions, regionCodes } = useAmazonRegionOptions(intl);

  const schema = Yup.object({
    value: Yup.string()
      .required(
        intl.formatMessage({
          id: "funding-application: valid marketplace hint",
          defaultMessage: "Please select an Amazon seller region.",
        })
      )
      .oneOf(regionCodes),
  });

  const amazonForm = useForm<AmazonData>({
    resolver: yupResolver(schema),
  });

  const connectHandler = async (formData: AmazonData) => {
    setIsSubmitting(true);
    const postPath = connectPostPath(customerDomain, CONNECTOR_SLUG, postConnectPath);
    if (postPath) {
      onInitiateConnection?.();
      await connectByPost(postPath, formData);
    }
  };

  return (
    <Flex direction="column" justify="flex-start">
      <Flex direction="column" gap="8">
        <OAuthErrorBanner connectorSlug={CONNECTOR_SLUG} />
        <Text>{intl.formatMessage(MESSAGES.ecommerceDataUsage, { platform: "Amazon" })}</Text>
        <ConnectionsList
          connectorProperties={connectorProperties}
          renderConnectAnother={(onConnectAnother) => (
            <Text size="sm">
              {intl.formatMessage(MESSAGES.connectAnotherStore, {
                link: (chunks: ReactNode) => <Link onClick={onConnectAnother}>{chunks}</Link>,
              })}
            </Text>
          )}
          connectForm={
            <form onSubmit={amazonForm.handleSubmit(connectHandler)}>
              <Flex direction="row" gap="3" align="center">
                <RhfDropdownField
                  label={intl.formatMessage({
                    id: "funding-application: seller region - label",
                    defaultMessage: "Select Amazon seller region",
                  })}
                  labelIcon={
                    connectorProperties.connectionLogoSrc && <Image src={connectorProperties.connectionLogoSrc} />
                  }
                  form={amazonForm}
                  name="value"
                  options={regionOptions}
                  inlineButton={{
                    label: intl.formatMessage({
                      id: "funding-application: log in to amazon button label",
                      defaultMessage: "Log in to Amazon",
                    }),
                    type: "submit",
                    loading: isSubmitting,
                  }}
                />
              </Flex>
            </form>
          }
        />
      </Flex>
    </Flex>
  );
};
